import { District } from '../../app/feathers/districts/District';

export type DistrictIdentifierType = 'name' | 'number' | 'letter';
export type DistrictIdentifierValue = string | number;

const identifierTypes: DistrictIdentifierType[] = ['name', 'number', 'letter'];

export const identifierTypeForDistrict = (
  district: Pick<District, 'name' | 'number' | 'letter'>,
): DistrictIdentifierType | null =>
  identifierTypes.find(type => district[type] !== undefined && district[type] !== null) || null;

export const initialIdentifierTypeForDistricts = (
  districts: Pick<District, 'name' | 'number' | 'letter'>[],
): DistrictIdentifierType =>
  districts.map(identifierTypeForDistrict).find((type): type is DistrictIdentifierType => Boolean(type)) || 'name';

export const identifierValueForDistrict = (
  district: Pick<District, 'name' | 'number' | 'letter'>,
): DistrictIdentifierValue | '' => {
  const type = identifierTypeForDistrict(district);
  return type ? district[type] ?? '' : '';
};

export const normalizeDistrictIdentifier = (
  value: DistrictIdentifierValue | null | undefined,
  type: DistrictIdentifierType,
): DistrictIdentifierValue | null => {
  const trimmed = `${value ?? ''}`.trim();
  if (!trimmed) return null;

  if (type === 'number') {
    return /^\d+$/.test(trimmed) && Number(trimmed) > 0
      ? Number(trimmed)
      : null;
  }

  if (type === 'letter') {
    return /^[A-Z]$/i.test(trimmed) ? trimmed.toUpperCase() : null;
  }

  return trimmed;
};

export interface IdentifierRow {
  identifier: DistrictIdentifierValue | null | undefined;
}

export interface IdentifierValidation {
  normalizedValues: (DistrictIdentifierValue | null)[];
  rowErrors: (string | null)[];
  isValid: boolean;
}

export const validateDistrictIdentifiers = (
  rows: IdentifierRow[],
  type: DistrictIdentifierType,
): IdentifierValidation => {
  const normalizedValues = rows.map(row => normalizeDistrictIdentifier(row.identifier, type));
  const counts = normalizedValues.reduce<Record<string, number>>((result, value) => {
    if (value !== null) result[`${value}`] = (result[`${value}`] || 0) + 1;
    return result;
  }, {});

  const rowErrors = normalizedValues.map(value => {
    if (value === null) {
      if (type === 'number') return 'Enter a positive whole number.';
      if (type === 'letter') return 'Enter one letter from A to Z.';
      return 'Enter a name.';
    }
    return counts[`${value}`] > 1 ? 'Identifier must be unique.' : null;
  });

  return {
    normalizedValues,
    rowErrors,
    isValid: rows.length > 0 && rowErrors.every(error => error === null),
  };
};

export const identifierPayload = (
  type: DistrictIdentifierType,
  value: DistrictIdentifierValue | null | undefined,
): Pick<Partial<District>, DistrictIdentifierType> => {
  const normalized = normalizeDistrictIdentifier(value, type);
  return normalized === null ? {} : { [type]: normalized };
};

export const identifierConversionSummary = (
  districts: Pick<District, 'name' | 'number' | 'letter'>[],
  selectedType: DistrictIdentifierType,
): string => {
  const conversionCounts = districts.reduce<Partial<Record<DistrictIdentifierType, number>>>((counts, district) => {
    const currentType = identifierTypeForDistrict(district);
    if (currentType && currentType !== selectedType) {
      counts[currentType] = (counts[currentType] || 0) + 1;
    }
    return counts;
  }, {});

  const conversions = identifierTypes
    .filter(type => conversionCounts[type])
    .map(type => `${conversionCounts[type]} ${type}`);
  const conversionCount = Object.values(conversionCounts)
    .reduce((total, count) => total + (count || 0), 0);

  return conversions.length
    ? `Converting ${conversions.join(' and ')} identifier${conversionCount === 1 ? '' : 's'} to ${selectedType}. Review the mapping below before saving.`
    : '';
};
